/**
 * SVDemo Theme — 32°-inspired
 * COPY TO: ecommerce-app/src/themes/svdemo.js
 *
 * Design ethos: extreme whitespace, products first, dark chrome, electric accent.
 * Models after 32degrees.com: near-black primary, white canvas, Inter font,
 * minimal navigation, full-bleed imagery, flat high-contrast CTAs.
 */

export default {
  name: 'svdemo',
  brandName: 'SchoolVision Demo Store',
  logoPath: 'svdemo-logo.png',

  colors: {
    headerColor:              '#1A1A2E',
    headerHover:              '#0F0F1E',
    frameColor:               '#E5E7EB',
    buttonColor:              '#1A1A2E',
    buttonHover:              '#0F0F1E',
    primaryActionBg:          '#1A1A2E',
    primaryActionBgHover:     '#0F0F1E',
    highlightButtonColor:     '#0EA5E9',
    highlightButtonHover:     '#0284C7',
    headerButtonColor:        '#FFFFFF',
    textColor:                '#111111',
    lightTextColor:           '#FFFFFF',
    buttonTextColor:          '#FFFFFF',
    headerTextColor:          '#FFFFFF',
    headerButtonTextColor:    '#FFFFFF',
    alertColor:               '#EF4444',
    alertHover:               '#DC2626',
    successColor:             '#22C55E',
    successHover:             '#16A34A',
    inactiveColor:            '#9CA3AF',
  },

  cssVariables: {
    // ─── Core Palette ───────────────────────────────────────────────
    '--header-color':             '#1A1A2E',  // Deep charcoal-navy
    '--header-hover':             '#0F0F1E',
    '--frame-color':              '#E5E7EB',  // Light gray dividers
    '--background-color':         '#FFFFFF',  // Pure white canvas
    '--card-bg':                  '#F9FAFB',  // Barely-off-white for cards
    '--card-border':              '#E5E7EB',

    // ─── Buttons ────────────────────────────────────────────────────
    '--button-color':             '#1A1A2E',
    '--button-hover':             '#0F0F1E',
    '--primary-action-bg':        '#1A1A2E',
    '--primary-action-bg-hover':  '#0F0F1E',
    '--highlight-button-color':   '#0EA5E9',  // Electric blue
    '--highlight-button-hover':   '#0284C7',
    '--header-button-color':      '#FFFFFF',

    // ─── Text ────────────────────────────────────────────────────────
    '--text-color':               '#111111',  // Near-black body
    '--light-text-color':         '#FFFFFF',
    '--button-text-color':        '#FFFFFF',
    '--header-text-color':        '#FFFFFF',
    '--header-button-text-color': '#FFFFFF',
    '--secondary-text-color':     '#6B7280',  // Gray for secondary copy

    // ─── Status ──────────────────────────────────────────────────────
    '--alert-color':              '#EF4444',
    '--alert-hover':              '#DC2626',
    '--success-color':            '#22C55E',
    '--success-hover':            '#16A34A',
    '--inactive-color':           '#9CA3AF',

    // ─── Product Grid ─────────────────────────────────────────────────
    '--shadow-product':           '0 2px 8px rgba(0,0,0,0.08)',
    '--shadow-product-hover':     '0 8px 24px rgba(0,0,0,0.12)',
    '--badge-bg':                 '#1A1A2E',
    '--badge-text':               '#FFFFFF',
    '--badge-accent-bg':          '#0EA5E9',

    // ─── Hero ─────────────────────────────────────────────────────────
    '--hero-overlay':             'rgba(26,26,46,0.48)',
    '--hero-text':                '#FFFFFF',

    // ─── Borders & Radius ─────────────────────────────────────────────
    '--border-radius-sm':         '2px',   // 32°-style: almost flat
    '--border-radius-md':         '4px',
    '--border-radius-lg':         '6px',
    '--border-color':             '#E5E7EB',
    '--border-color-dark':        '#D1D5DB',

    // ─── Typography ───────────────────────────────────────────────────
    '--font-family':              '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
    '--font-size-base':           '16px',
    '--font-weight-normal':       '400',
    '--font-weight-medium':       '500',
    '--font-weight-semibold':     '600',
    '--letter-spacing-wide':      '0.05em',  // Used for uppercase labels/badges

    // ─── Spacing ──────────────────────────────────────────────────────
    '--header-height':            '56px',
    '--promo-banner-height':      '36px',
    '--grid-gap':                 '1rem',    // 16px product grid gap

    // ─── Transitions ──────────────────────────────────────────────────
    '--transition-fast':          '150ms ease',
    '--transition-normal':        '300ms ease',
    '--image-crossfade':          '300ms ease-in-out',
  },

  /**
   * Veneer component overrides.
   * Maps default component name → svdemo replacement component path.
   * Consumed by src/themes/veneer/index.js (componentRegistry).
   */
  veneerOverrides: {
    AppHeader:       'svdemo/SVDemoHeader',
    WindermereHeader: 'svdemo/SVDemoHeader',
    WindermereHero:  'svdemo/SVDemoHero',
    PromoBanner:     'svdemo/SVDemoPromoBanner',
    FeaturedItemCard: 'svdemo/SVDemoProductCard',
    AppFooter:       'svdemo/SVDemoFooter',
  },

  /**
   * Feature flag overrides for this theme.
   * These supplement (not replace) the window config flags.
   */
  featureFlags: {
    showHero:            true,
    showFeaturedBadges:  true,
    quickAddEnabled:     true,
    imageHoverCrossfade: true,
    stickyHeader:        true,
    megaMenu:            false,   // simplified for campus store
    demoModeBanner:      true,
  }
}
